<script>
	import { Chart, registerables } from "chart.js";
	import "chartjs-adapter-date-fns";

	// Chart.js 등록
	Chart.register(...registerables);
	import { onMount, afterUpdate } from "svelte";
	import { Y_AXIS_TICKS, Y_AXIS_TICK_LABELS } from "../utils/logParser.js";

	export let chartKey = "";
	export let chartData = [];
	export let legendFontSize = 10;
	export let labelFontSize = 8;

	let canvas;
	let chart;

	const createChart = () => {
		if (!canvas) return;

		// 기존 차트 제거
		if (chart) {
			chart.destroy();
		}

		const ctx = canvas.getContext("2d");

		// 데이터와 라벨 타입 판별
		const yValues = chartData.map((point) => point.y);
		const yLabels = Y_AXIS_TICK_LABELS[chartKey];

		const dataType = getDataType(yValues);
		const labelType = getLabelType(yLabels);

		console.log("chartKey: %s, dataType: %s, labelType: %s", chartKey, dataType, labelType);

		let scaleConfig;
		let processedData = chartData;

		// 경우별 분기처리
		if (dataType === "numeric" && labelType === "numeric") {
			// 경우 1: 값 숫자, 라벨 숫자
			scaleConfig = getLinearConfig(chartData, chartKey);
		} else if (dataType === "numeric" && labelType === "string") {
			// 경우 2: 값 숫자, 라벨 문자
			const linearWithLabelsConfig = getLinearWithLabelsConfig(chartData, chartKey);
			scaleConfig = linearWithLabelsConfig;
			processedData = linearWithLabelsConfig.data;
		} else if (dataType === "string" && labelType === "empty") {
			// 경우 3: 값 문자, 라벨 문자
			const categoryConfig = getCategoryConfig(chartData, chartKey);
			scaleConfig = categoryConfig;
			processedData = categoryConfig.data;
		} else {
			// 기본값 (혼합 타입이나 예외 상황)
			scaleConfig = getLinearConfig(chartData, chartKey);
		}

		const config = {
			type: "line",
			data: {
				datasets: [
					{
						label: chartKey,
						data: processedData,
						borderColor: getChartColor(chartKey),
						backgroundColor: getChartColor(chartKey, 0.2),
						borderWidth: 1,
						pointRadius: 1,
						tension: 0,
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: {
						position: "top",
						labels: { font: { size: legendFontSize } },
					},
				},
				scales: {
					x: {
						type: "time",
						bounds: "ticks",
						time: {
							displayFormats: {
								hour: "MM/dd HH:mm",
							},
							tooltipFormat: "yyyy-MM-dd HH:mm:ss",
						},
						ticks: { font: { size: labelFontSize } },
						grid: { display: true },
					},
					...scaleConfig.scales,
				},
			},
		};

		// 여백 적용
		config.options.scales.y.grace = "10%";
		config.options.scales.x.grace = "10%";
		config.options.scales.y.offset = true;

		chart = new Chart(ctx, config);
	};

	const getChartColor = (key, alpha = 1) => {
		const colors = {
			Battery_L: `rgba(255, 99, 132, ${alpha})`,
			Battery_R: `rgba(54, 162, 235, ${alpha})`,
			Sound_BGM: `rgba(255, 205, 86, ${alpha})`,
			Sound_dbbeats: `rgba(75, 192, 192, ${alpha})`,
			Sound_stimulus: `rgba(153, 102, 255, ${alpha})`,
			Action: `rgba(255, 159, 64, ${alpha})`,
			"PPG/ACC": `rgba(199, 199, 199, ${alpha})`,
			BLE: `rgba(83, 102, 255, ${alpha})`,
			ANC: `rgba(255, 99, 255, ${alpha})`,
			RSSI: `rgba(255, 159, 132, ${alpha})`,
			Earbud: `rgba(102, 255, 178, ${alpha})`,
			RoleSwitch: `rgba(255, 178, 102, ${alpha})`,
			SleepStage: `rgba(178, 102, 255, ${alpha})`,
		};
		return colors[key] || `rgba(75, 192, 192, ${alpha})`;
	};

	const getLinearConfig = (chartData, chartKey) => {
		return {
			scales: {
				y: {
					type: "linear",
					reverse: false,
					ticks: {
						font: { size: labelFontSize },
					},
					grid: { display: true },
				},
			},
		};
	};

	const getLinearWithLabelsConfig = (chartData, chartKey) => {
		const labels = Y_AXIS_TICK_LABELS[chartKey];
		const ticks = Y_AXIS_TICKS[chartKey];

		// 숫자 데이터를 커스텀 라벨로 변환
		const convertedData = chartData.map((point) => {
			return {
				x: point.x,
				y: labels[ticks.indexOf(point.y)],
			};
		});

		return {
			data: convertedData,
			scales: {
				y: {
					type: "category",
					labels: labels,
					reverse: false,
					ticks: {
						font: { size: labelFontSize },
					},
					grid: { display: true },
				},
			},
		};
	};

	const getCategoryConfig = (chartData, chartKey) => {
		// 중복 제거하여 고유한 문자열 값들을 라벨로 사용
		const uniqueLabels = [...new Set(chartData.map((point) => point.y))];

		return {
			data: chartData,
			scales: {
				y: {
					type: "category",
					labels: uniqueLabels,
					reverse: false,
					ticks: {
						font: { size: labelFontSize },
					},
					grid: { display: true },
				},
			},
		};
	};

	const getDataType = (data) => {
		if (!data || data.length === 0) return "empty";
		const sample = data[0];
		return typeof sample === "number" || !isNaN(sample) ? "numeric" : "string";
	};

	const getLabelType = (labels) => {
		if (!labels || labels.length === 0) return "empty";
		const sample = labels[0];
		return typeof sample === "number" || !isNaN(sample) ? "numeric" : "string";
	};

	onMount(() => {
		if (chartData.length > 0) {
			createChart();
		}
	});

	afterUpdate(() => {
		if (chartData.length > 0) {
			createChart();
		}
	});
</script>

<canvas bind:this={canvas}></canvas>

<style>
	canvas {
		display: block;
	}
</style>
